import type { ReactNode } from 'react'
import {
  Rocket,
  FolderOpen,
  Bot,
  Users,
  Film,
  Sparkles,
  Share2,
  Mic,
  Captions,
  type LucideIcon,
} from 'lucide-react'

export interface DocsSection {
  id: string
  heading: string
  content: ReactNode
  bullets?: string[]
  tip?: string
  image?: {
    src: string
    alt: string
    caption?: string
  }
}

export interface DocsArticle {
  slug: string
  title: string
  description: string
  icon: LucideIcon
  sections: DocsSection[]
}

export interface DocsCategory {
  label: string
  articles: DocsArticle[]
}

export const categories: DocsCategory[] = [
  {
    label: 'Core Workflow',
    articles: [
      {
        slug: 'getting-started',
        title: 'Getting Started',
        description: 'Overview of Footage and your first steps.',
        icon: Rocket,
        sections: [
          {
            id: 'what-is-footage',
            heading: 'What is Footage?',
            content: (
              <>
                Footage is an <strong>AI-powered video creation studio</strong> that turns your ideas into polished video projects. Describe what you want to see, and Footage writes creative briefs, generates clips, and refines every frame.
              </>
            ),
          },
          {
            id: 'creating-an-account',
            heading: 'Creating an Account',
            content: (
              <>
                Sign up with your <strong>Google account</strong> — no separate password needed. You'll land in the studio immediately with free credits to start creating.
              </>
            ),
            bullets: [
              'Click "Sign up" on the homepage',
              'Authenticate with Google',
              'You\'re in — start your first project',
            ],
            tip: 'Footage is also available as a desktop app for Mac. Download it from the homepage for a native experience.',
          },
          {
            id: 'studio-overview',
            heading: 'Studio Overview',
            content: (
              <>
                The studio is your home base. It shows all your projects with their current status, lets you create new ones, and gives quick access to assets, templates, and settings from the sidebar.
              </>
            ),
          },
        ],
      },
      {
        slug: 'projects',
        title: 'Videos',
        description: 'Creating and managing your videos.',
        icon: FolderOpen,
        sections: [
          {
            id: 'creating-a-project',
            heading: 'Creating a Video',
            content: (
              <>
                Click <strong>"New video"</strong> in the studio to get started. Give your video a name and optionally describe the overall narrative you have in mind.
              </>
            ),
          },
          {
            id: 'director-vs-manual',
            heading: 'Director Mode vs Manual Mode',
            content: (
              <>
                Footage offers two ways to build your video. <strong>Director mode</strong> lets Footage plan your entire video — it writes the narrative, creates scenes, and generates clips automatically. <strong>Manual mode</strong> gives you full control: you add clips, write prompts, and choose models yourself.
              </>
            ),
            bullets: [
              'Director mode: describe your vision, AI handles the rest',
              'Manual mode: full creative control over every clip',
              'Switch between modes at any time',
            ],
            tip: 'Start with Director mode if you\'re not sure what you want yet — you can always refine manually later.',
          },
          {
            id: 'project-settings',
            heading: 'Project Settings',
            content: (
              <>
                Each project has settings for aspect ratio, default AI model, and narrative style. Access these from the project header or through chat.
              </>
            ),
          },
        ],
      },
      {
        slug: 'ai-agent',
        title: 'AI Director',
        description: 'Using the chat-based creative director.',
        icon: Bot,
        sections: [
          {
            id: 'chat-interface',
            heading: 'The Chat Interface',
            content: (
              <>
                Footage lives in the chat panel on the left side of the project editor. It acts as your <strong>creative director</strong> — describe what you want, and it plans scenes, writes prompts, and manages your timeline.
              </>
            ),
          },
          {
            id: 'what-the-agent-can-do',
            heading: 'What Footage Can Do',
            content: (
              <>
                Footage understands your creative intent and translates it into concrete video clips. It can plan entire narratives, suggest visual styles, create and edit clips, and manage characters.
              </>
            ),
            bullets: [
              'Plan multi-scene narratives from a single description',
              'Create clips with optimized prompts for AI video models',
              'Edit existing clips — change prompts, reorder, or remove',
              'Define characters with consistent appearances across scenes',
              'Suggest creative directions and visual styles',
            ],
          },
          {
            id: 'suggested-actions',
            heading: 'Suggested Actions',
            content: (
              <>
                After Footage responds, you'll often see <strong>suggested actions</strong> — quick buttons to generate clips, extend the narrative, or make adjustments. These save you from typing common follow-up requests.
              </>
            ),
            tip: 'You can always type your own instructions instead of using suggested actions. Footage understands natural language.',
          },
        ],
      },
      {
        slug: 'characters',
        title: 'Characters',
        description: 'Defining characters with reference images and voices.',
        icon: Users,
        sections: [
          {
            id: 'defining-characters',
            heading: 'Defining Characters',
            content: (
              <>
                Characters keep appearances consistent across your entire project. Define a character once — their look, clothing, features — and the AI applies that description to every clip they appear in.
              </>
            ),
          },
          {
            id: 'reference-images',
            heading: 'Reference Images',
            content: (
              <>
                Upload a <strong>reference image</strong> for each character. The AI uses this to maintain visual consistency — matching face shape, hair color, clothing style, and other distinctive features across all generated clips.
              </>
            ),
            tip: 'Use clear, well-lit photos for the best character consistency. Front-facing shots work best as references.',
          },
          {
            id: 'character-voices',
            heading: 'Character Voices',
            content: (
              <>
                Assign a voice to each character for voiceover and dialog. Choose from the built-in voice library or clone a custom voice. When Footage writes dialog for a character, it automatically uses their assigned voice.
              </>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'Video Generation',
    articles: [
      {
        slug: 'timeline',
        title: 'Timeline',
        description: 'Working with the timeline editor and clips.',
        icon: Film,
        sections: [
          {
            id: 'timeline-overview',
            heading: 'Timeline Overview',
            content: (
              <>
                The timeline is the visual backbone of your project. Each clip appears as a card showing its prompt, thumbnail, and generation status. Drag to reorder, click to select, and use the toolbar for bulk actions.
              </>
            ),
          },
          {
            id: 'adding-clips',
            heading: 'Adding Clips',
            content: (
              <>
                Add clips through Footage (it creates them from your description) or manually by clicking <strong>"Add Clip"</strong> in the timeline toolbar. Each clip has a text prompt that describes the scene.
              </>
            ),
            bullets: [
              'AI-created clips come with optimized prompts',
              'Manual clips let you write your own prompt from scratch',
              'Duplicate existing clips to create variations',
            ],
          },
          {
            id: 'scenes-and-storyboards',
            heading: 'Scenes & Storyboards',
            content: (
              <>
                Group related clips into <strong>scenes</strong> for better organization. The storyboard view shows your entire project at a glance — perfect for reviewing narrative flow before generating.
              </>
            ),
            tip: 'Use scenes to organize longer projects. Footage can automatically group clips into logical scenes.',
          },
        ],
      },
      {
        slug: 'video-generation',
        title: 'Video Generation',
        description: 'AI models, aspect ratios, and generation settings.',
        icon: Sparkles,
        sections: [
          {
            id: 'ai-models',
            heading: 'AI Models',
            content: (
              <>
                Footage integrates multiple AI video generation models, each with different strengths. Some excel at photorealistic footage, others at stylized or animated content. Choose the model that matches your project's aesthetic.
              </>
            ),
          },
          {
            id: 'aspect-ratios',
            heading: 'Aspect Ratios & Duration',
            content: (
              <>
                Set the aspect ratio per-project or per-clip. Common options include <strong>16:9</strong> (landscape), <strong>9:16</strong> (portrait/mobile), and <strong>1:1</strong> (square). Clip duration depends on the model — most support 5 to 10 seconds per clip.
              </>
            ),
            bullets: [
              '16:9 — YouTube, presentations, widescreen',
              '9:16 — TikTok, Reels, Stories',
              '1:1 — Instagram feed, social media',
            ],
          },
          {
            id: 'regenerating',
            heading: 'Regenerating & Iterating',
            content: (
              <>
                Not happy with a clip? <strong>Regenerate</strong> it with the same prompt for a different take, or edit the prompt and generate again. Each generation creates a new version — previous versions are kept so you can compare and pick the best.
              </>
            ),
            tip: 'You can regenerate individual clips without affecting the rest of your timeline. Use the "Regenerate" button on any clip card.',
          },
        ],
      },
      {
        slug: 'sharing',
        title: 'Sharing & Export',
        description: 'Share links, embedding, and exporting your video.',
        icon: Share2,
        sections: [
          {
            id: 'share-links',
            heading: 'Share Links',
            content: (
              <>
                Every project gets a <strong>public share link</strong> that anyone can view without signing in. The share page shows your video with a clean player, project title, and description.
              </>
            ),
          },
          {
            id: 'embedding',
            heading: 'Embedding',
            content: (
              <>
                Embed your video on any website using the embed code. The lightweight player loads fast and supports autoplay, looping, and responsive sizing.
              </>
            ),
          },
          {
            id: 'exporting',
            heading: 'Exporting',
            content: (
              <>
                Export your finished project as a single video file. Footage stitches all clips together, applies voiceovers and captions, and renders the final output. Download the result or share it directly.
              </>
            ),
            tip: 'Export renders happen in the cloud — you can close the browser and come back when it\'s done.',
          },
        ],
      },
    ],
  },
  {
    label: 'Audio & Voices',
    articles: [
      {
        slug: 'voiceovers',
        title: 'Voiceovers',
        description: 'Text-to-speech, voice picker, and dialog.',
        icon: Mic,
        sections: [
          {
            id: 'text-to-speech',
            heading: 'Text-to-Speech',
            content: (
              <>
                Add voiceovers to any clip by writing dialog text. Footage converts it to natural-sounding speech using AI text-to-speech. Each clip can have its own voice or use the project default.
              </>
            ),
          },
          {
            id: 'voice-library',
            heading: 'Voice Library',
            content: (
              <>
                Browse the built-in voice library to find the perfect voice for your project. Filter by language, accent, gender, and tone. Preview any voice before selecting it.
              </>
            ),
            bullets: [
              'Dozens of built-in voices across multiple languages',
              'Preview voices with sample text before selecting',
              'Assign different voices to different characters',
            ],
          },
          {
            id: 'dialog-per-clip',
            heading: 'Dialog Per Clip',
            content: (
              <>
                Write dialog directly on each clip in the timeline. Footage can also write dialog for you based on your narrative — it assigns the right character voice automatically.
              </>
            ),
            tip: 'When using Director mode, Footage writes dialog that matches each character\'s personality and voice.',
          },
        ],
      },
      {
        slug: 'lip-sync',
        title: 'Lip Sync',
        description: 'Lip-sync post-processing for characters.',
        icon: Users,
        sections: [
          {
            id: 'how-lip-sync-works',
            heading: 'How Lip Sync Works',
            content: (
              <>
                Lip sync is an AI post-processing step that <strong>matches character mouth movements</strong> to the voiceover audio. After a clip is generated and has a voiceover, apply lip sync to make the character appear to speak naturally.
              </>
            ),
          },
          {
            id: 'applying-lip-sync',
            heading: 'Applying Lip Sync',
            content: (
              <>
                Enable lip sync on any clip that has both a generated video and a voiceover. The process runs automatically in the cloud and typically takes a few seconds. The result replaces the original clip video.
              </>
            ),
            tip: 'Lip sync works best with clear frontal face shots. Profile or distant views may produce less accurate results.',
          },
        ],
      },
      {
        slug: 'captions',
        title: 'Captions',
        description: 'Transcription and word-by-word captions.',
        icon: Captions,
        sections: [
          {
            id: 'automatic-transcription',
            heading: 'Automatic Transcription',
            content: (
              <>
                Footage automatically transcribes voiceover audio into <strong>word-by-word captions</strong>. Captions are timed precisely to the audio, making your videos accessible and engaging.
              </>
            ),
          },
          {
            id: 'caption-styles',
            heading: 'Caption Styles',
            content: (
              <>
                Choose from multiple caption styles — standard subtitles, bold highlight words, or one-word-at-a-time animations. Customize font, size, color, and position to match your project's look.
              </>
            ),
            bullets: [
              'Multiple built-in caption styles',
              'Customize font, size, color, and position',
              'Word-by-word highlight animation',
            ],
          },
          {
            id: 'editing-captions',
            heading: 'Editing Captions',
            content: (
              <>
                Edit transcribed text directly if the AI gets a word wrong. Adjust timing, fix proper nouns, or rephrase for clarity. Changes update the visual captions in real time.
              </>
            ),
            tip: 'Captions also improve your video\'s SEO and accessibility when shared publicly.',
          },
        ],
      },
    ],
  },
]
